import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  serverTimestamp, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { sendLeaveApprovalNotification, sendClaimApprovalNotification } from '../../emailjs/emailService';

const LEAVES_COLLECTION = 'leaves';
const CLAIMS_COLLECTION = 'claims';

// Async thunk to fetch pending leaves
export const fetchPendingLeaves = createAsyncThunk(
  'approvals/fetchPendingLeaves',
  async (statusFilter = 'Pending', { rejectWithValue }) => {
    try {
      const leavesRef = collection(db, LEAVES_COLLECTION);
      let leavesQuery;
      
      if (statusFilter === 'All') {
        leavesQuery = query(leavesRef);
      } else {
        leavesQuery = query(leavesRef, where('status', '==', statusFilter));
      }
      
      const querySnapshot = await getDocs(leavesQuery);
      
      const leaves = [];
      querySnapshot.forEach(doc => {
        leaves.push({ id: doc.id, ...doc.data() });
      });
      
      return leaves;
    } catch (error) {
      console.error('Error fetching leaves:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch pending claims
export const fetchPendingClaims = createAsyncThunk(
  'approvals/fetchPendingClaims',
  async (statusFilter = 'Pending', { rejectWithValue }) => {
    try {
      const claimsRef = collection(db, CLAIMS_COLLECTION);
      let claimsQuery;
      
      if (statusFilter === 'All') {
        claimsQuery = query(claimsRef);
      } else {
        claimsQuery = query(claimsRef, where('status', '==', statusFilter));
      }
      
      const querySnapshot = await getDocs(claimsQuery);
      
      const claims = [];
      querySnapshot.forEach(doc => {
        claims.push({ id: doc.id, ...doc.data() });
      });
      
      return claims;
    } catch (error) {
      console.error('Error fetching claims:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to approve/reject a leave request
export const updateLeaveStatus = createAsyncThunk(
  'approvals/updateLeaveStatus',
  async ({ leaveId, status, managerRemarks }, { rejectWithValue, getState }) => {
    try {
      const leaveRef = doc(db, LEAVES_COLLECTION, leaveId);
      const leaveDoc = await getDoc(leaveRef);
      
      if (!leaveDoc.exists()) {
        throw new Error('Leave request not found');
      }
      
      const leaveData = leaveDoc.data();
      
      await updateDoc(leaveRef, {
        status,
        managerRemarks,
        lastUpdated: serverTimestamp(),
        updatedBy: 'admin' // In a real app, you'd use the admin's ID
      });
      
      // Send email notification to the employee
      try {
        const state = getState();
        const employees = state.auth.employees;
        
        // Find the employee data
        const employeeEmail = leaveData.empEmail || leaveData.email;
        const employee = employees.find(emp => emp.email === employeeEmail);
        
        if (employee) {
          await sendLeaveApprovalNotification(
            { ...leaveData, id: leaveId },
            employee,
            status,
            managerRemarks
          );
          console.log('Leave approval notification sent successfully');
        } else {
          console.warn('Employee not found for leave approval notification:', employeeEmail);
        }
      } catch (emailError) {
        console.error('Failed to send leave approval notification:', emailError);
        // Don't fail the whole operation if email fails
      }
      
      // Return the updated document with the local timestamp for immediate UI update
      return {
        id: leaveId,
        status,
        managerRemarks,
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Error updating leave status:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to approve/reject a claim
export const updateClaimStatus = createAsyncThunk(
  'approvals/updateClaimStatus',
  async ({ claimId, status, managerRemarks }, { rejectWithValue, getState }) => {
    try {
      const claimRef = doc(db, CLAIMS_COLLECTION, claimId);
      const claimDoc = await getDoc(claimRef);
      
      if (!claimDoc.exists()) {
        throw new Error('Claim request not found');
      }
      
      const claimData = claimDoc.data();
      
      await updateDoc(claimRef, {
        status,
        managerRemarks,
        lastUpdated: serverTimestamp(),
        updatedBy: 'admin' // In a real app, you'd use the admin's ID
      });
      
      // Send email notification to the employee
      try {
        const state = getState();
        const employees = state.auth.employees;
        
        // Find the employee data
        const employeeEmail = claimData.empEmail || claimData.email;
        const employee = employees.find(emp => emp.email === employeeEmail);
        
        if (employee) {
          await sendClaimApprovalNotification(
            { ...claimData, id: claimId },
            employee,
            status,
            managerRemarks
          );
          console.log('Claim approval notification sent successfully');
        } else {
          console.warn('Employee not found for claim approval notification:', employeeEmail);
        }
      } catch (emailError) {
        console.error('Failed to send claim approval notification:', emailError);
        // Don't fail the whole operation if email fails
      }
      
      // Return the updated document with the local timestamp for immediate UI update
      return {
        id: claimId,
        status,
        managerRemarks,
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Error updating claim status:', error);
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  leaves: {
    data: [],
    loading: false,
    error: null,
    updatingId: null
  },
  claims: {
    data: [],
    loading: false,
    error: null,
    updatingId: null
  }
};

const approvalsSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.leaves.error = null;
      state.claims.error = null;
    }
  },
  extraReducers: (builder) => {
    // Handle pending leaves fetch
    builder.addCase(fetchPendingLeaves.pending, (state) => {
      state.leaves.loading = true;
      state.leaves.error = null;
    });
    builder.addCase(fetchPendingLeaves.fulfilled, (state, action) => {
      state.leaves.loading = false;
      state.leaves.data = action.payload;
    });
    builder.addCase(fetchPendingLeaves.rejected, (state, action) => {
      state.leaves.loading = false;
      state.leaves.error = action.payload;
    });

    // Handle pending claims fetch
    builder.addCase(fetchPendingClaims.pending, (state) => {
      state.claims.loading = true;
      state.claims.error = null;
    });
    builder.addCase(fetchPendingClaims.fulfilled, (state, action) => {
      state.claims.loading = false;
      state.claims.data = action.payload;
    });
    builder.addCase(fetchPendingClaims.rejected, (state, action) => {
      state.claims.loading = false;
      state.claims.error = action.payload;
    });

    // Handle leave status update
    builder.addCase(updateLeaveStatus.pending, (state, action) => {
      state.leaves.updatingId = action.meta.arg.leaveId;
    });
    builder.addCase(updateLeaveStatus.fulfilled, (state, action) => {
      state.leaves.updatingId = null;
      state.leaves.data = state.leaves.data.filter(leave => leave.id !== action.meta.arg.leaveId);
    });
    builder.addCase(updateLeaveStatus.rejected, (state, action) => {
      state.leaves.updatingId = null;
      state.leaves.error = action.payload;
    });

    // Handle claim status update
    builder.addCase(updateClaimStatus.pending, (state, action) => {
      state.claims.updatingId = action.meta.arg.claimId;
    });
    builder.addCase(updateClaimStatus.fulfilled, (state, action) => {
      state.claims.updatingId = null;
      state.claims.data = state.claims.data.filter(claim => claim.id !== action.meta.arg.claimId);
    });
    builder.addCase(updateClaimStatus.rejected, (state, action) => {
      state.claims.updatingId = null;
      state.claims.error = action.payload;
    });
  }
});

export const { clearErrors } = approvalsSlice.actions;
export default approvalsSlice.reducer; 